import type { CoverSubtype } from "@march/shared";

/**
 * Curated display order for Cover SKUs in the Forecast view — March's
 * merchandising order (by frame size, then fabric code), which isn't
 * derivable from the data, so it's listed explicitly.
 *
 * One list per subtype. On the Cover tab the groups are shown in the
 * order Olefin -> Pacifica -> Sunbrella, and each group is ordered by its
 * list. SKUs absent from a list sort alphabetically at the end of their
 * own group, so a newly-catalogued cover is never hidden.
 *
 * Sunbrella (CoverBase) has no curated list — those rows are synthetic
 * fabric-code rollups, so they fall back to alphabetical.
 */
export const OLEFIN_SORT_ORDER: readonly string[] = [
  "C908-F03",
  "C908-F04",
  "C908-F08",
  "C908-F09",
  "C908-F13",
  "C908-F22",
  "C908-F25",
  "C908-F26",
  "C908-F27",
  "C908-F32",
  "C908-F55",
  "C908-F67",
  "C908-F69",
  "C908-F71",
  "C908-F72",
  "C908-F76",
  "C908-F77",
  "C908-F95",
  "C908-F96",
  "C908-FD10",
  "C908-FD11",
  "C908-FD12",
  "C118-F03-SWV",
  "C118-F04-SWV",
  "C118-F08-SWV",
  "C118-F09-SWV",
  "C118-F13-SWV",
  "C118-F22-SWV",
  "C118-F25-SWV",
  "C118-F26-SWV",
  "C118-F27-SWV",
  "C118-F32-SWV",
  "C118-F55-SWV",
  "C118-F67-SWV",
  "C118-F69-SWV",
  "C118-F71-DWV",
  "C118-F72-DWV",
  "C118-F76-DWV",
  "C118-F77-SWV",
  "C118-FD10-DWV",
  "C118-FD11-DWV",
  "C118-FD12-DWV",
  "C758-F03",
  "C758-F04",
  "C758-F08",
  "C758-F09",
  "C758-F13",
  "C758-F22",
  "C758-F25",
  "C758-F26",
  "C758-F27",
  "C758-F32",
  "C758-F55",
  "C758-F67",
  "C758-F69",
  "C758-F71",
  "C758-F72",
  "C758-F76",
  "C758-F77",
  "C758-F95",
  "C758-F96",
  "C758-FD10",
  "C758-FD11",
  "C758-FD12",
];

export const PACIFICA_SORT_ORDER: readonly string[] = [
  "C908-SA01",
  "C908-SA03",
  "C908-SA04",
  "C908-SA08",
  "C908-SA11",
  "C908-SA13",
  "C908-SA14",
  "C908-SA17",
  "C908-SA21",
  "C908-SA22",
  "C908-SA26",
  "C908-SA26-DWV",
  "C908-SA32",
  "C908-SA36",
  "C908-SA39",
  "C908-SA40",
  "C908-SA46",
  "C908-SA52",
  "C908-SA53",
  "C908-SA57",
  "C908-SA61",
  "C908-SA65",
  "C118-SA01-SWV",
  "C118-SA01-DWV",
  "C118-SA03-SWV",
  "C118-SA03-DWV",
  "C118-SA04-SWV",
  "C118-SA04-DWV",
  "C118-SA08-SWV",
  "C118-SA08-DWV",
  "C118-SA11-SWV",
  "C118-SA11-DWV",
  "C118-SA13-SWV",
  "C118-SA13-DWV",
  "C118-SA14-DWV",
  "C118-SA17-SWV",
  "C118-SA17-DWV",
  "C118-SA21-SWV",
  "C118-SA21-DWV",
  "C118-SA22-SWV",
  "C118-SA22-DWV",
  "C118-SA26-SWV",
  "C118-SA26-DWV",
  "C118-SA32-SWV",
  "C118-SA32-DWV",
  "C118-SA36-SWV",
  "C118-SA36-DWV",
  "C118-SA39-SWV",
  "C118-SA39-DWV",
  "C118-SA40-SWV",
  "C118-SA40-DWV",
  "C118-SA46-SWV",
  "C118-SA46-DWV",
  "C118-SA52-SWV",
  "C118-SA52-DWV",
  "C118-SA53-SWV",
  "C118-SA53-DWV",
  "C118-SA57-SWV",
  "C118-SA57-DWV",
  "C118-SA61-SWV",
  "C118-SA61-DWV",
  "C118-SA65-SWV",
  "C118-SA65-DWV",
  "C758-SA01",
  "C758-SA03",
  "C758-SA04",
  "C758-SA08",
  "C758-SA11",
  "C758-SA13",
  "C758-SA14",
  "C758-SA17",
  "C758-SA21",
  "C758-SA22",
  "C758-SA26",
  "C758-SA32",
  "C758-SA36",
  "C758-SA39",
  "C758-SA40",
  "C758-SA46",
  "C758-SA52",
  "C758-SA53",
  "C758-SA57",
  "C758-SA61",
  "C758-SA65",
  "C852PD-SA01/SA04",
  "C852PD-SA03/SA04",
  "C852PD-SA04/SA08",
];

/**
 * Sunbrella base-pattern order, taken from the plan's own Sunbrella tab
 * row order (SWV/DWV collapsed, since a CoverBase key covers the pair).
 * Keys the plan lists but CoverBase cannot express (C1188, C852PD,
 * C852CLAS) are kept here so the order stays faithful if they are ever
 * tracked; unlisted keys fall to the bottom as before.
 */
export const SUNBRELLA_SORT_ORDER: readonly string[] = [
  "C908-5401",
  "C908-5402",
  "C908-5403",
  "C908-5404",
  "C908-5405",
  "C908-5406",
  "C908-5407",
  "C908-5408",
  "C908-5410",
  "C908-5413",
  "C908-5414",
  "C908-5415",
  "C908-5416",
  "C908-5417",
  "C908-5422",
  "C908-5425",
  "C908-5429",
  "C908-5432",
  "C908-5439",
  "C908-5440",
  "C908-5446",
  "C908-5453",
  "C908-5457",
  "C908-5461",
  "C908-5462",
  "C908-5476",
  "C908-5488",
  "C908-5493",
  "C908-5499",
  "C908-5608",
  "C908-8318",
  "C908-48020",
  "C908-48022",
  "C908-48032",
  "C908-48080",
  "C908-48090",
  "C908-48095",
  "C908-54048",
  "C908-56000",
  "C908-56001",
  "C908-56095",
  "C908-56096",
  "C908-56105",
  "C908-57002",
  "C908-58029",
  "C908-58030",
  "C908-58038",
  "C908-58039",
  "C118-5401",
  "C118-5402",
  "C118-5403",
  "C118-5404",
  "C118-5405",
  "C118-5406",
  "C118-5407",
  "C118-5408",
  "C118-5410",
  "C118-5413",
  "C118-5414",
  "C118-5415",
  "C118-5416",
  "C118-5417",
  "C118-5422",
  "C118-5425",
  "C118-5429",
  "C118-5432",
  "C118-5439",
  "C118-5440",
  "C118-5446",
  "C118-5453",
  "C118-5457",
  "C118-5461",
  "C118-5462",
  "C118-5476",
  "C118-5488",
  "C118-5493",
  "C118-5499",
  "C118-5608",
  "C118-8318",
  "C118-48020",
  "C118-48022",
  "C118-48032",
  "C118-48080",
  "C118-48090",
  "C118-48095",
  "C118-54048",
  "C118-56000",
  "C118-56001",
  "C118-56095",
  "C118-56096",
  "C118-56105",
  "C118-57002",
  "C118-58029",
  "C118-58030",
  "C118-58038",
  "C118-58039",
  "C758-5401",
  "C758-5402",
  "C758-5403",
  "C758-5404",
  "C758-5405",
  "C758-5406",
  "C758-5407",
  "C758-5408",
  "C758-5410",
  "C758-5413",
  "C758-5414",
  "C758-5415",
  "C758-5416",
  "C758-5417",
  "C758-5422",
  "C758-5425",
  "C758-5429",
  "C758-5432",
  "C758-5439",
  "C758-5440",
  "C758-5446",
  "C758-5453",
  "C758-5457",
  "C758-5461",
  "C758-5462",
  "C758-5476",
  "C758-5488",
  "C758-5493",
  "C758-5499",
  "C758-5608",
  "C758-8318",
  "C758-48020",
  "C758-48022",
  "C758-48032",
  "C758-48080",
  "C758-48090",
  "C758-48095",
  "C758-54048",
  "C758-56000",
  "C758-56001",
  "C758-56095",
  "C758-56096",
  "C758-56105",
  "C758-57002",
  "C758-58029",
  "C758-58030",
  "C758-58038",
  "C758-58039",
  "C608-5401",
  "C608-5403",
  "C608-5404",
  "C608-5408",
  "C608-5417",
  "C608-5422",
  "C608-5429",
  "C608-5439",
  "C608-5446",
  "C608-5457",
  "C604-5401",
  "C604-5403",
  "C604-5404",
  "C604-5408",
  "C604-5417",
  "C604-5422",
  "C604-5429",
  "C604-5439",
  "C604-5446",
  "C604-5457",
  "C1188-5401",
  "C1188-5403",
  "C1188-5404",
  "C1188-5408",
  "C1188-5417",
  "C1188-5422",
  "C1188-5429",
  "C1188-5439",
  "C1188-5446",
  "C1188-5457",
  "C852PD-0401",
  "C852PD-0422",
  "C852PD-0403",
  "C852PD-0446",
  "C852PD-0417",
  "C852PD-0416",
  "C852PD-0439",
  "C852PD-0457",
  "C852PD-0429",
  "C852PD-0408",
  "C852CLAS-5401-BND503016",
  "C852CLAS-5403-BND503016",
  "C852CLAS-5404-BND503016",
  "C852CLAS-5408-BND503016",
  "C852CLAS-5417-BND503016",
  "C852CLAS-5422-BND503016",
  "C852CLAS-5429-BND503016",
  "C852CLAS-5439-BND503016",
  "C852CLAS-5446-BND503016",
  "C852CLAS-5457-BND503016",
];

const OLEFIN_INDEX = new Map(OLEFIN_SORT_ORDER.map((s, i) => [s, i]));
const PACIFICA_INDEX = new Map(PACIFICA_SORT_ORDER.map((s, i) => [s, i]));
const SUNBRELLA_INDEX = new Map(SUNBRELLA_SORT_ORDER.map((s, i) => [s, i]));

/** Which block a cover belongs to: Olefin, then Pacifica, then Sunbrella. */
export function coverGroupRank(subtype: CoverSubtype | undefined): number {
  switch (subtype) {
    case "Olefin":
      return 0;
    case "Pacifica":
      return 1;
    case "Sunbrella":
      return 2;
    default:
      return 3; // untyped covers — after the known blocks
  }
}

/**
 * Position within the SKU's own block. Unlisted SKUs get a rank past the
 * end so they land at the bottom of their group, where the caller
 * alphabetizes them.
 */
export function coverSortRank(
  itemid: string,
  subtype: CoverSubtype | undefined,
): number {
  const index =
    subtype === "Olefin"
      ? OLEFIN_INDEX
      : subtype === "Pacifica"
        ? PACIFICA_INDEX
        : subtype === "Sunbrella"
          ? SUNBRELLA_INDEX
          : null;
  return index?.get(itemid) ?? Number.MAX_SAFE_INTEGER;
}
